import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'
import { Tag } from 'components/v2/Tag'
import { IFareFamiliesFiltersProps, IFareFamiliesFilters } from 'features/FareFamilies/views/Index/Filters/types'
import { SegmentAutocomplete } from 'features/Segments/components/SegmentAutocomplete'
import { getQueryString } from 'utils/route'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { GTSegmentSuggestion } from 'gql/types.generated'

const messages = defineMessages({
  selectCompany: {
    id: 'fareFamilies.filters.selectCompany',
    defaultMessage: 'Select company'
  },
  selectSegment: {
    id: 'fareFamilies.filters.selectSegment',
    defaultMessage: 'Select segment'
  },
  selectOriginCity: {
    id: 'fareFamilies.filters.selectOriginCity',
    defaultMessage: 'Select origin city'
  },
  selectDestinationCity: {
    id: 'fareFamilies.filters.selectDestinationCity',
    defaultMessage: 'Select destination city'
  }
})

const defaultValues = {
  company: { id: '', name: '' },
  segment: { id: '', name: '' },
  originCity: { id: '', name: '' },
  destinationCity: { id: '', name: '' }
}

export const FareFamiliesFilters = ({ filters }: IFareFamiliesFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): TSFixMe => {
    showFilters(!isShowingFilters)
  }

  const filterFareFamilies = (values: IFareFamiliesFilters): TSFixMe => {
    const query = {
      companyId: values.company.id,
      segmentId: values.segment.id,
      originCityId: values.originCity.id,
      destinationCityId: values.destinationCity.id
    }

    navigate(`/fare-families?${getQueryString(query)}`)
  }

  const initialValues = {
    company: filters.company || defaultValues.company,
    segment: filters.segment || defaultValues.segment,
    originCity: filters.originCity || defaultValues.originCity,
    destinationCity: filters.destinationCity || defaultValues.destinationCity
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterFareFamilies} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values } = props

        const handleFilterDelete = (id: keyof IFareFamiliesFilters): TSFixMe => {
          setFieldValue(id, defaultValues[id])

          filterFareFamilies({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterWrapper>
                <FilterToggle onClick={toggleFilters} />
                <TagsWrapper>
                  {keys(filters).map((filterKey): ReactElement | null => {
                    let label, value

                    if (isEmpty(filters[filterKey as keyof typeof defaultValues])) return null

                    switch (filterKey) {
                      case 'company':
                        label = <FormattedMessage id="dictionary.company" defaultMessage="Company" />
                        value = filters.company.name
                        break
                      case 'segment':
                        label = <FormattedMessage id="dictionary.segment" defaultMessage="Segment" />
                        value = filters.segment.name
                        break
                      case 'originCity':
                        label = <FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />
                        value = filters.originCity.name
                        break
                      case 'destinationCity':
                        label = <FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />
                        value = filters.destinationCity.name
                        break
                    }

                    if (!value) return null

                    return (
                      <Tag
                        key={filterKey}
                        onDelete={handleFilterDelete.bind(this, filterKey as keyof IFareFamiliesFilters)}
                      >
                        {label}: {value}
                      </Tag>
                    )
                  })}
                </TagsWrapper>
              </FilterWrapper>
              <FiltersWrapper isShowingFilters={isShowingFilters} noBorder noPadding>
                <FiltersContainer>
                  <CompanyAutocomplete
                    placeholder={intl.formatMessage(messages.selectCompany)}
                    value={values.company.name}
                    label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                    onChange={(company: TSFixMe): TSFixMe => {
                      setFieldValue('company', company)

                      filterFareFamilies({
                        ...values,
                        company: company
                      })
                    }}
                  />
                  <SegmentAutocomplete
                    placeholder={intl.formatMessage(messages.selectSegment)}
                    value={values.segment.name}
                    label={<FormattedMessage id="dictionary.segment" defaultMessage="Segment" />}
                    onChange={(segment: GTSegmentSuggestion): void => {
                      setFieldValue('segment', segment)

                      filterFareFamilies({
                        ...values,
                        segment
                      })
                    }}
                  />
                  <CityAutocomplete
                    placeholder={intl.formatMessage(messages.selectOriginCity)}
                    value={values.originCity.name}
                    label={<FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />}
                    onChange={(city: TSFixMe): void => {
                      setFieldValue('originCity', city)

                      filterFareFamilies({
                        ...values,
                        originCity: city
                      })
                    }}
                  />
                  <CityAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestinationCity)}
                    value={values.destinationCity.name}
                    label={<FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />}
                    onChange={(city: TSFixMe): void => {
                      setFieldValue('destinationCity', city)

                      filterFareFamilies({
                        ...values,
                        destinationCity: city
                      })
                    }}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
